import React, { useCallback, useMemo } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChangeTenant,
  useGetMyTenants,
} from '@area-restrita/api-client/endpoints/auth/auth.gen';
import { ChangeTenantBody } from '@area-restrita/api-client/model/changeTenantBody';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

interface TenantSwitcherProps {}

const TenantSwitcher: React.FC<TenantSwitcherProps> = () => {
  const { isMobile } = useSidebar();
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const { signIn, session } = useAuthSessionStore();
  const { data: tenants } = useGetMyTenants();

  const activeTenant = useMemo(() => {
    if (!session?.organization) return null;

    const name =
      session.organization.company?.companyName ?? session.organization.name;

    return {
      name,
      aditionalInfo: session.organization.company
        ? session.organization.name
        : 'Administradora',
      logo: `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(name)}`,
    };
  }, [session?.organization]);

  const { mutate: changeTenantMutate } = useChangeTenant({
    mutation: {
      onSuccess: ({ accessToken }) => {
        queryClient.invalidateQueries();
        router.invalidate();
        signIn(accessToken);
        navigate({ to: '/' });
      },
    },
  });

  const handleSelectTenant = useCallback(
    (tenant: ChangeTenantBody) => changeTenantMutate({ data: tenant }),
    [changeTenantMutate],
  );

  if (!activeTenant) return null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={activeTenant.logo} alt={activeTenant.name} />
                <AvatarFallback className="rounded-lg" />
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {activeTenant.name}
                </span>
                <span className="truncate text-xs">
                  {activeTenant.aditionalInfo}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Organizações
            </DropdownMenuLabel>

            {!tenants?.length && (
              <DropdownMenuItem disabled className="text-center">
                Nenhuma organização encontrada
              </DropdownMenuItem>
            )}
            {tenants?.map((tenant) => (
              <DropdownMenuGroup key={tenant.slug}>
                {!tenant.isTenantUser && (
                  <DropdownMenuLabel>{tenant.name}</DropdownMenuLabel>
                )}

                {tenant.isTenantUser && (
                  <DropdownMenuItem
                    className="p-2 flex items-center justify-between w-full gap-2 font-semibold"
                    onClick={() =>
                      handleSelectTenant({ tenantSlug: tenant.slug })
                    }
                  >
                    <span>{tenant.name}</span>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      Admin
                    </span>
                  </DropdownMenuItem>
                )}

                {tenant.companies.map((company) => (
                  <DropdownMenuItem
                    key={company.id}
                    className="p-2 pl-10"
                    onClick={() =>
                      handleSelectTenant({
                        tenantSlug: tenant.slug,
                        companyId: company.id,
                      })
                    }
                  >
                    {company.tradingName}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

export default TenantSwitcher;
